import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PRICING } from "@/constants/testIds";
import {
  ShieldCheck, Settings2, Headphones, Crown,
  Users2, CalendarCheck, ClipboardList, MessageSquare,
  MonitorPlay, Bell, BarChart3, Building2, User, Flag,
  Clock, ChevronDown, MousePointerClick, Award, Check,
} from "lucide-react";

/* ---------- TIER DATA — matches the "Choose Your Level of Support" graphic ---------- */
const TIERS = [
  {
    step: 1,
    percent: "3%",
    name: "PLATFORM",
    accent: "#94A3B8",              // blue
    accentBg: "rgba(148,163,184,0.10)",
    accentBorder: "rgba(148,163,184,0.4)",
    Icon: ShieldCheck,
    deployedBy: "YOU RUN THE DEPLOYMENT",
    hours: "0 HRS",
    benefit: "ACCESS THE PLATFORM",
    features: [
      { Icon: Users2, label: "Access to talent pool" },
      { Icon: CalendarCheck, label: "Job scheduling" },
      { Icon: ClipboardList, label: "Booking management" },
      { Icon: MessageSquare, label: "In-app messaging" },
    ],
    testId: "pricing-tier-platform",
    dropdown: {
      forWho: "Security companies and end-clients confident to manage their own operations who just need a modern platform to source, book and pay verified staff.",
      included: [
        "Unlimited access to the vetted talent pool (Ireland + UK)",
        "Post jobs, receive applicants, hand-pick teams",
        "Basic scheduling, in-app messaging & document sharing",
        "Automated invoicing & payments",
        "Standard support (24-48h response)",
      ],
      excluded: [
        "No deployment software / live tracking",
        "No dedicated operations advisor",
        "No compliance monitoring",
      ],
    },
  },
  {
    step: 2,
    percent: "5%",
    name: "OPERATIONS",
    accent: "#D4A24C",              // teal
    accentBg: "rgba(212,162,76,0.08)",
    accentBorder: "rgba(212,162,76,0.4)",
    Icon: Settings2,
    deployedBy: "YOU RUN THE DEPLOYMENT",
    hours: "0 HRS",
    benefit: "POWERFUL DEPLOYMENT SOFTWARE",
    features: [
      { Icon: MonitorPlay, label: "Deployment dashboard" },
      { Icon: Users2, label: "Team roster & clusters" },
      { Icon: ClipboardList, label: "Live task board" },
      { Icon: Bell, label: "Alerts & notifications" },
      { Icon: ShieldCheck, label: "Incident logging" },
      { Icon: BarChart3, label: "Reporting analytics" },
    ],
    testId: "pricing-tier-operations",
    dropdown: {
      forWho: "Growing security firms and event organisers who want professional-grade deployment software but keep operational control in-house.",
      included: [
        "Everything in Platform (3%)",
        "Ops Room dashboard with live check-in map",
        "Cluster team builder with Lead Pro / Buddy invites",
        "Real-time GPS check-in, shift alerts, incident log",
        "Deployment reports & downloadable audit trail",
        "Priority support (12-24h response)",
      ],
      excluded: [
        "No advisory phone-time from SecuSource",
        "No proactive compliance monitoring",
      ],
    },
  },
  {
    step: 3,
    percent: "10%",
    name: "ASSISTED ELITE",
    accent: "#FF7200",              // orange (brand)
    accentBg: "rgba(255,114,0,0.10)",
    accentBorder: "rgba(255,114,0,0.55)",
    Icon: Headphones,
    deployedBy: "WE ADVISE & MONITOR",
    hours: "UP TO 2 HRS",
    benefit: "EXPERT ADVICE & COMPLIANCE SUPPORT",
    featured: true,
    features: [
      { Icon: MonitorPlay, label: "Deployment dashboard" },
      { Icon: Users2, label: "Team roster & clusters" },
      { Icon: ClipboardList, label: "Live task board" },
      { Icon: Bell, label: "Alerts & notifications" },
      { Icon: ShieldCheck, label: "Incident logging" },
      { Icon: BarChart3, label: "Reporting analytics" },
      { Icon: Headphones, label: "Elite advisory support" },
    ],
    testId: "pricing-tier-assisted-elite",
    dropdown: {
      forWho: "Companies handling higher-risk deployments (executive events, sensitive sites, close protection) who want expert eyes and compliance oversight — without handing over full ops control.",
      included: [
        "Everything in Operations (5%)",
        "Up to 2 hours per deployment of Elite Operations Director advisory time",
        "Pre-deployment risk review + SOP sign-off",
        "Live monitoring of check-ins by SecuSource ops",
        "Compliance & audit report on completion",
        "Dedicated Slack / WhatsApp channel with your account manager",
      ],
      excluded: [
        "SecuSource does not directly manage your officers on-site",
        "No 24/7 command centre coverage",
      ],
    },
  },
  {
    step: 4,
    percent: "15%",
    name: "MANAGED ELITE",
    accent: "#C084FC",              // purple
    accentBg: "rgba(192,132,252,0.08)",
    accentBorder: "rgba(192,132,252,0.45)",
    Icon: Crown,
    deployedBy: "WE MANAGE THE DEPLOYMENT",
    hours: "UNLIMITED",
    benefit: "FULL OPERATIONAL MANAGEMENT",
    features: [
      { Icon: MonitorPlay, label: "Deployment dashboard" },
      { Icon: Users2, label: "Team roster & clusters" },
      { Icon: ClipboardList, label: "Live task board" },
      { Icon: Bell, label: "Alerts & notifications" },
      { Icon: ShieldCheck, label: "Incident logging" },
      { Icon: BarChart3, label: "Reporting analytics" },
      { Icon: Headphones, label: "24/7 elite ops centre" },
      { Icon: Users2, label: "Handpicked global team" },
    ],
    testId: "pricing-tier-managed-elite",
    dropdown: {
      forWho: "End-clients and principals who want a turnkey security operation — SecuSource handles sourcing, briefing, deployment, live command and reporting from start to finish.",
      included: [
        "Everything in Assisted Elite (10%)",
        "Unlimited command-centre hours across the deployment",
        "SecuSource-selected & briefed team (CPOs, drivers, RST)",
        "24/7 dedicated Elite Operations Director assigned to your engagement",
        "Real-time global tracking, contingency planning, exfil support",
        "White-glove reporting suite delivered to your board / principal",
      ],
      excluded: [],
    },
  },
];

const BOTTOM_STRIP = [
  { Icon: MousePointerClick, label: "ACCESS THE PLATFORM", accent: "#94A3B8" },
  { Icon: BarChart3, label: "POWERFUL DEPLOYMENT SOFTWARE", accent: "#D4A24C" },
  { Icon: Headphones, label: "EXPERT ADVICE & COMPLIANCE SUPPORT", accent: "#FF7200" },
  { Icon: Crown, label: "FULL OPERATIONAL MANAGEMENT", accent: "#C084FC" },
];

/* ---------- MAIN PAGE ---------- */
export default function Pricing() {
  const [openTier, setOpenTier] = useState(null);

  return (
    <div className="bg-[#0A0A0A] text-white">
      {/* Header — mirrors graphic (logo left · title · Mick Miller right) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-10 pb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Left: SecuSource lockup */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md flex items-center justify-center"
                 style={{ background: "#FF7200", boxShadow: "0 8px 22px -6px rgba(255,114,0,0.65)" }}>
              <ShieldCheck className="w-6 h-6 text-black" strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-outfit font-bold text-white text-xl leading-none">SecuSource</div>
              <div className="text-[10px] uppercase tracking-widest mt-1 font-outfit">
                <span className="text-white/85">Better Teams.</span>{" "}
                <span className="text-[#FF7200]">Faster Deployment.</span>
              </div>
            </div>
          </div>

          {/* Right: Mick Miller badge */}
          <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] backdrop-blur px-3 py-2 max-w-sm">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1a0f04] to-[#3a2410] flex items-center justify-center border border-[#FF7200]/40 flex-shrink-0">
              <Award className="w-5 h-5 text-[#FF7200]" />
            </div>
            <div className="min-w-0">
              <div className="font-outfit font-bold text-white text-sm leading-tight">MICK MILLER</div>
              <div className="text-[10px] uppercase tracking-widest text-[#FF7200] font-outfit font-bold leading-tight mt-0.5">
                Elite Operations Director
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit leading-tight mt-0.5">
                30+ Years CPO Experience
              </div>
            </div>
          </div>
        </div>

        {/* Big title */}
        <div className="text-center mt-10">
          <h1 className="font-outfit font-black tracking-tight leading-none text-white"
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
              data-testid="pricing-title">
            CHOOSE YOUR LEVEL OF SUPPORT
          </h1>
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="h-[2px] w-24 bg-[#FF7200]" />
            <ShieldCheck className="w-5 h-5 text-[#FF7200]" />
            <div className="h-[2px] w-24 bg-[#FF7200]" />
          </div>
          <p className="mt-5 max-w-2xl mx-auto text-white/70 font-manrope text-sm sm:text-base">
            Four tiers. One professional standard. Pick the level of hands-on support that fits your deployment — you can upgrade any time.
          </p>
        </div>
      </section>

      {/* Companies-free + Client-number promo */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-4 pb-2">
        <div className="rounded-2xl overflow-hidden relative border p-5 sm:p-6 grid md:grid-cols-2 gap-5"
             style={{
               borderColor: "rgba(255,114,0,0.4)",
               background: "linear-gradient(135deg, rgba(255,114,0,0.10) 0%, rgba(10,10,10,0.7) 60%, #0A0A0A 100%)",
             }}
             data-testid="pricing-free-promo">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full blur-2xl" style={{ background: "rgba(255,114,0,0.20)" }} aria-hidden="true" />
          <div className="relative flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: "rgba(255,114,0,0.20)", border: "1px solid rgba(255,114,0,0.5)" }}>
              <Building2 className="w-6 h-6" style={{ color: "#FF7200" }} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-outfit font-bold mb-1.5"
                   style={{ background: "rgba(255,114,0,0.15)", borderColor: "rgba(255,114,0,0.4)", color: "#FF7200" }}>
                Free forever
              </div>
              <h3 className="font-outfit font-bold text-white text-lg leading-tight">
                Companies register for <span style={{ color: "#FF7200" }}>free</span>
              </h3>
              <p className="text-sm text-white/70 font-manrope mt-1.5">
                No sign-up fee. No monthly subscription. No credit card required.
                You only pay the transparent commission when you actually book someone.
              </p>
            </div>
          </div>
          <div className="relative flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: "rgba(255,114,0,0.20)", border: "1px solid rgba(255,114,0,0.5)" }}>
              <ClipboardList className="w-6 h-6" style={{ color: "#FF7200" }} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-outfit font-bold mb-1.5"
                   style={{ background: "rgba(255,114,0,0.15)", borderColor: "rgba(255,114,0,0.4)", color: "#FF7200" }}>
                Unique Client Number
              </div>
              <h3 className="font-outfit font-bold text-white text-lg leading-tight">
                Every job gets a Client Job Number (e.g. <span style={{ color: "#FF7200" }}>JOB-0007</span>)
              </h3>
              <p className="text-sm text-white/70 font-manrope mt-1.5">
                Full traceability from post to payout. Reference on invoices, in disputes, on incident reports, and across the audit trail — for you, your officers and your clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Four tier cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-10">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((tier) => (
            <TierCard
              key={tier.name}
              tier={tier}
              isOpen={openTier === tier.name}
              onToggle={() => setOpenTier(openTier === tier.name ? null : tier.name)}
            />
          ))}
        </div>
      </section>

      {/* Bottom feature strip — mirrors graphic */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-8">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {BOTTOM_STRIP.map(({ Icon, label, accent }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md flex items-center justify-center"
                   style={{ background: `${accent}1F`, border: `1px solid ${accent}66`, color: accent }}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-widest font-outfit font-bold text-white/90 leading-tight">
                {label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Loyalty promotion — try 5% Operations after 3 basic bookings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-10">
        <div className="rounded-2xl overflow-hidden relative border p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
             style={{
               borderColor: "rgba(212,162,76,0.4)",
               background: "linear-gradient(135deg, rgba(212,162,76,0.08) 0%, rgba(6,16,23,0.7) 60%, rgba(6,16,23,1) 100%)",
             }}
             data-testid="pricing-loyalty-promo">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full blur-2xl" style={{ background: "rgba(212,162,76,0.20)" }} aria-hidden="true" />
          <div className="relative w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
               style={{ background: "rgba(212,162,76,0.20)", border: "1px solid rgba(212,162,76,0.5)" }}>
            <Settings2 className="w-6 h-6" style={{ color: "#D4A24C" }} />
          </div>
          <div className="relative flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 border rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-outfit font-bold mb-1.5"
                 style={{ background: "rgba(212,162,76,0.15)", borderColor: "rgba(212,162,76,0.4)", color: "#D4A24C" }}>
              Loyalty upgrade
            </div>
            <h3 className="font-outfit font-bold text-white text-lg sm:text-xl leading-tight">
              Try the 5% Deployment model — <span style={{ color: "#D4A24C" }}>free for one booking after your 3rd basic booking</span>
            </h3>
            <p className="text-sm text-white/70 font-manrope mt-1.5">
              Complete three bookings on the 3% Platform tier and we'll upgrade your next deployment to 5% Operations at no extra fee. Experience live tracking, Ops Room dashboards and cluster teams — see if it's the right fit for you.
            </p>
          </div>
          <Button asChild className="relative flex-shrink-0 font-outfit font-bold uppercase tracking-widest text-xs px-5 py-5"
                  style={{ background: "#D4A24C", color: "#1A1300" }} data-testid="pricing-loyalty-cta">
            <a href="/register/company">Start earning it</a>
          </Button>
        </div>
      </section>

      {/* Instant fee calculator — kept below the tier picker */}
      <PricingCalculator />
    </div>
  );
}

/* ---------- Individual tier card with dropdown ---------- */
function TierCard({ tier, isOpen, onToggle }) {
  const {
    step, percent, name, accent, accentBg, accentBorder, Icon,
    deployedBy, hours, benefit, features, featured, dropdown, testId,
  } = tier;

  return (
    <div
      data-testid={testId}
      className={`relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#141414] to-[#0A0A0A] transition
        ${featured ? "shadow-[0_0_0_1px_rgba(255,114,0,0.55),0_20px_60px_-20px_rgba(255,114,0,0.35)]" : "border border-white/10"}
      `}
      style={featured ? { background: "linear-gradient(180deg, #1a0f04 0%, #0f0a06 55%, #0A0A0A 100%)" } : undefined}
    >
      {/* Step badge (top-left) */}
      <div className="absolute top-3 left-3 w-8 h-8 rounded-full font-outfit font-bold text-white flex items-center justify-center text-sm"
           style={{ background: accent, boxShadow: `0 0 0 3px ${accentBg}, 0 0 20px -4px ${accent}` }}>
        {step}
      </div>

      {/* Percent + name */}
      <div className="text-center pt-6 pb-5 px-5">
        <div className="font-outfit font-black leading-none tracking-tight"
             style={{ fontSize: "clamp(2.5rem, 5vw, 3.75rem)", color: "#FFFFFF" }}>
          {percent}
        </div>
        <div className="mt-2 font-outfit font-bold uppercase tracking-widest text-sm"
             style={{ color: accent }}>
          {name}
        </div>
        <div className="mt-4 flex justify-center">
          <div className="w-14 h-14 rounded-md flex items-center justify-center"
               style={{ background: accentBg, border: `1px solid ${accentBorder}`, color: accent }}>
            <Icon className="w-7 h-7" />
          </div>
        </div>
      </div>

      {/* Feature icon row */}
      <div className="px-4 pb-4">
        <div className="rounded-lg py-3 px-2 grid grid-cols-4 gap-x-2 gap-y-3 justify-items-center"
             style={{ background: accentBg, border: `1px solid ${accentBorder}` }}>
          {features.slice(0, 8).map(({ Icon: FIcon, label }, i) => (
            <div key={i} className="flex flex-col items-center gap-1" title={label}>
              <FIcon className="w-4 h-4" style={{ color: accent }} />
              <Check className="w-3 h-3" style={{ color: accent }} strokeWidth={3} />
            </div>
          ))}
        </div>
      </div>

      {/* Deployment illustration: Building → Person → Building */}
      <div className="px-5 pb-3">
        <div className="flex items-center justify-between opacity-90">
          <Building2 className="w-6 h-6" style={{ color: accent }} />
          <div className="flex-1 mx-2 border-t border-dashed" style={{ borderColor: `${accent}55` }} />
          <User className="w-6 h-6" style={{ color: accent }} />
          <div className="flex-1 mx-2 border-t border-dashed" style={{ borderColor: `${accent}55` }} />
          <Flag className="w-6 h-6" style={{ color: accent }} />
        </div>
        <div className="mt-2 text-center text-[10px] uppercase tracking-widest font-outfit font-bold text-white/85">
          {deployedBy}
        </div>
      </div>

      {/* Hours pill */}
      <div className="px-5">
        <div className="mx-auto max-w-[180px] rounded-md py-2 px-3 flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-outfit font-bold"
             style={{ background: accentBg, border: `1px solid ${accentBorder}`, color: accent }}>
          <Clock className="w-3.5 h-3.5" /> {hours}
        </div>
      </div>

      {/* Benefit strip */}
      <div className="mt-4 px-5 py-3 text-center border-t border-white/10"
           style={{ background: accentBg }}>
        <div className="text-[11px] uppercase tracking-widest font-outfit font-bold" style={{ color: accent }}>
          {benefit}
        </div>
      </div>

      {/* Dropdown toggle */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-center gap-1.5 py-3 text-xs uppercase tracking-widest font-outfit font-bold text-white/80 hover:text-white transition border-t border-white/10"
        data-testid={`${testId}-toggle`}
      >
        {isOpen ? "Hide details" : "See what's included"}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown content */}
      {isOpen && (
        <div className="px-5 pb-5 space-y-4 border-t border-white/10 pt-4" data-testid={`${testId}-details`}>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-outfit font-bold mb-1.5" style={{ color: accent }}>
              Best for
            </div>
            <p className="text-xs text-white/80 font-manrope leading-relaxed">
              {dropdown.forWho}
            </p>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest font-outfit font-bold mb-2" style={{ color: accent }}>
              What's included
            </div>
            <ul className="space-y-1.5">
              {dropdown.included.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-white/85 font-manrope">
                  <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: accent }} strokeWidth={3} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {dropdown.excluded.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-outfit font-bold mb-2 text-white/50">
                Not included
              </div>
              <ul className="space-y-1.5">
                {dropdown.excluded.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-white/55 font-manrope">
                    <span className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-white/40">—</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button asChild className="w-full font-outfit font-bold uppercase tracking-widest text-xs"
                  style={{ background: accent, color: "#000" }} data-testid={`${testId}-cta`}>
            <a href="/register/company">Get started with {percent}</a>
          </Button>
        </div>
      )}
    </div>
  );
}

/* ---------- Tier options for the calculator dropdown ---------- */
const CALC_TIERS = [
  { value: 0.03, label: "3% Platform (Security Deployment)", desc: "Access the platform — you run the deployment. Best for small teams and confident operators.", accent: "#94A3B8" },
  { value: 0.05, label: "5% Operations (Security Deployment)", desc: "Powerful deployment software — best for larger teams, cluster ops and event contractors.", accent: "#D4A24C" },
  { value: 0.10, label: "10% Assisted Elite", desc: "Expert advice & compliance support — best for higher-risk and executive deployments.", accent: "#FF7200" },
  { value: 0.15, label: "15% Managed Elite", desc: "Full operational management — best for principals and end-clients wanting a turnkey elite team.", accent: "#C084FC" },
];

/* ---------- Calculator (retained, styled dark, now with tier picker) ---------- */
function PricingCalculator() {
  const [tierIdx, setTierIdx] = useState(0);
  const [workers, setWorkers] = useState(5);
  const [hours, setHours] = useState(10);
  const [rate, setRate] = useState(20);
  const [quote, setQuote] = useState(null);

  const tier = CALC_TIERS[tierIdx];

  const calculate = () => {
    const w = Math.max(1, Number(workers) || 0);
    const h = Math.max(0.5, Number(hours) || 0);
    const r = Math.max(1, Number(rate) || 0);
    const booking_value = +(w * h * r).toFixed(2);
    const per_hour_fee = +(w * h * 1).toFixed(2);
    const tier_fee = +(booking_value * tier.value).toFixed(2);

    // Platform (3%) only: apply the LOWER of €1/hr vs 3% (business rule).
    // Higher tiers apply the tier percentage in full.
    let applied_fee = tier_fee;
    let fee_method = `${(tier.value * 100).toFixed(0)}% of booking`;
    if (tier.value === 0.03 && per_hour_fee < tier_fee) {
      applied_fee = per_hour_fee;
      fee_method = "€1/hour (lower of the two)";
    }
    setQuote({ booking_value, per_hour_fee, tier_fee, applied_fee, fee_method, tier });
  };

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-20">
      <div className="text-center mb-8">
        <span className="inline-block text-[10px] uppercase tracking-[0.25em] text-[#FF7200] font-outfit font-bold">
          Instant quote
        </span>
        <h2 className="mt-2 font-outfit font-bold text-white text-2xl sm:text-3xl">
          Preview your deployment fee
        </h2>
        <p className="mt-2 text-white/60 font-manrope text-sm">Pick a tier below and enter your job details for a live estimate.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <div className="text-xs uppercase tracking-widest text-[#FF7200] font-outfit font-bold mb-4">Calculator</div>

          <div className="space-y-4">
            {/* Tier dropdown */}
            <div>
              <Label htmlFor="calc-tier" className="font-manrope text-white/80">Deployment tier</Label>
              <select
                id="calc-tier"
                value={tierIdx}
                onChange={(e) => { setTierIdx(Number(e.target.value)); setQuote(null); }}
                data-testid="pricing-tier-select"
                className="mt-1 w-full rounded-md bg-black/30 border border-white/15 text-white px-3 py-2 text-sm font-manrope focus:outline-none focus:border-[#FF7200] appearance-none"
                style={{
                  backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23FF7200' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "12px",
                }}
              >
                {CALC_TIERS.map((t, i) => (
                  <option key={t.label} value={i}>{t.label}</option>
                ))}
              </select>
              <p className="mt-2 text-xs font-manrope leading-relaxed" style={{ color: tier.accent }}>
                {tier.desc}
              </p>
            </div>

            <div>
              <Label htmlFor="workers" className="font-manrope text-white/80">Number of workers</Label>
              <Input id="workers" type="number" min={1} value={workers}
                onChange={(e) => setWorkers(Number(e.target.value))}
                data-testid={PRICING.workersInput}
                className="mt-1 bg-black/30 border-white/15 text-white" />
            </div>
            <div>
              <Label htmlFor="hours" className="font-manrope text-white/80">Hours per worker</Label>
              <Input id="hours" type="number" min={1} step="0.5" value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                data-testid={PRICING.hoursInput}
                className="mt-1 bg-black/30 border-white/15 text-white" />
            </div>
            <div>
              <Label htmlFor="rate" className="font-manrope text-white/80">Hourly rate (€)</Label>
              <Input id="rate" type="number" min={1} step="0.5" value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                data-testid={PRICING.rateInput}
                className="mt-1 bg-black/30 border-white/15 text-white" />
            </div>
            <Button onClick={calculate}
              className="w-full py-6 font-outfit font-bold uppercase tracking-widest text-sm text-black hover:opacity-90"
              style={{ background: tier.accent }}
              data-testid={PRICING.calculateBtn}>
              Calculate {(tier.value * 100).toFixed(0)}% fee
            </Button>
          </div>
        </div>

        <div className="rounded-2xl p-6 border"
             style={{
               borderColor: `${tier.accent}66`,
               background: `linear-gradient(180deg, ${tier.accent}14 0%, #0A0A0A 70%)`,
             }}
             data-testid={PRICING.result}>
          <div className="text-xs uppercase tracking-widest font-outfit font-bold mb-2" style={{ color: tier.accent }}>Result · {tier.label.split(" (")[0]}</div>
          {quote ? (
            <>
              <div className="text-white/70 mb-2 font-manrope">Booking value</div>
              <div className="text-4xl font-outfit font-bold text-white mb-6">€{quote.booking_value}</div>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/10 p-4 rounded">
                  <div className="text-xs text-white/60 uppercase tracking-wider font-outfit">€1/hr fee</div>
                  <div className="text-xl font-outfit font-bold text-white">€{quote.per_hour_fee}</div>
                </div>
                <div className="bg-white/10 p-4 rounded">
                  <div className="text-xs text-white/60 uppercase tracking-wider font-outfit">{(tier.value * 100).toFixed(0)}% fee</div>
                  <div className="text-xl font-outfit font-bold text-white">€{quote.tier_fee}</div>
                </div>
              </div>
              <div className="border-t border-white/10 pt-4">
                <div className="text-xs uppercase tracking-widest font-outfit font-bold" style={{ color: tier.accent }}>Applied fee · {quote.fee_method}</div>
                <div className="text-5xl font-outfit font-bold text-white mt-2">€{quote.applied_fee}</div>
              </div>
            </>
          ) : (
            <div className="text-white/70 font-manrope">Pick a tier, enter details and calculate to see the applied fee.</div>
          )}
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-white/50 font-manrope">
        <strong className="text-white/80">Platform (3%) rule:</strong> we charge whichever is lower — €1/hr or 3% of the booking value. Higher tiers charge the tier % of the full booking value.
      </p>
    </section>
  );
}
